/**
 * 진행 중인 로그인 시도 표시.
 *
 * Google OAuth 는 앱 밖으로 나갔다 돌아오는 전체 페이지 이동이라, 클릭과 성공이
 * 서로 다른 문서에서 일어난다. 두 지점을 잇기 위해 시작 시각을 남겨 둔다.
 *
 * `sessionStorage` 가 아니라 `localStorage` 를 쓴다. 인증하러 나가 있는 동안
 * 저사양 현장 단말은 OS 메모리 압박으로 웹뷰째 정리되는 일이 잦은데, 그러면
 * sessionStorage 는 통째로 사라진다. 돌아와서 로그인은 성공했는데 **성공 이벤트만
 * 유실되므로**, 하필 단말이 약한 사용자 집단에서 로그인 전환율이 실제보다 낮게 잡힌다.
 * 탭 간 공유라는 부작용이 있지만, 표시는 읽는 즉시 소비되므로 중복 보고로 번지지 않는다.
 *
 * 이 표시가 남은 채로 로그인 화면이 다시 뜨면 = 인증을 마치지 못하고 돌아온 것이다.
 */
const LOGIN_ATTEMPT_KEY = "bricks:login-started-at";

/**
 * 이 시간을 넘긴 표시는 "진행 중인 시도"로 보지 않는다.
 *
 * 영속 저장으로 옮긴 대가로, 며칠 전 포기한 시도의 표시가 그대로 남아 있을 수 있다.
 * 그걸 소요 시간으로 그대로 쓰면 며칠짜리 값이 분포에 섞여 중앙값까지 망가진다.
 */
const ATTEMPT_TTL_MS = 30 * 60 * 1000;

export type LoginAttempt = {
  /**
   * 시도 시작으로부터 지난 시간(ms).
   *
   * TTL 을 넘겼으면 "로그인 시도가 있었다"는 사실만 믿고 소요 시간은 버린다(`null`).
   */
  durationMs: number | null;
};

export function markLoginAttempt(): void {
  try {
    localStorage.setItem(LOGIN_ATTEMPT_KEY, String(Date.now()));
  } catch {
    // 프라이빗 모드 등 저장 실패 시 소요 시간 측정만 포기한다.
  }
}

/**
 * 진행 중이던 시도를 가져오면서 지운다.
 *
 * 성공/이탈 어느 쪽이든 한 번만 보고돼야 하므로, 읽는 쪽이 곧 소유권을 가져간다.
 *
 * @returns 시도 표시가 없으면 `null`.
 */
export function takeLoginAttempt(): LoginAttempt | null {
  try {
    const startedAt = localStorage.getItem(LOGIN_ATTEMPT_KEY);
    if (startedAt === null) return null;

    localStorage.removeItem(LOGIN_ATTEMPT_KEY);

    const parsed = Number(startedAt);
    if (!Number.isFinite(parsed)) return { durationMs: null };

    const elapsed = Math.max(0, Date.now() - parsed);
    return { durationMs: elapsed > ATTEMPT_TTL_MS ? null : elapsed };
  } catch {
    return null;
  }
}
